import api from 'api';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { LTHeader, LTItem, LTPlaceholder, LTMobileItem } from 'components/common/ListTable';
import Checkbox from 'components/common/Checkbox/Checkbox';
import { CustomLTRow } from 'views/Dashboards-B2B/components/customLTRow/customLTRow';
import { Ticket } from 'api/models/response/Ticket';
import { TicketStatus } from 'api/models/payload/Ticket';
import { loadDocs } from 'redux/application/documents/documents-middleware';
import { RootState, AppDispatch } from 'redux/store';
import {
    Modal,
    ModalHeader,
    Form,
    Section,
    Actions,
    SecondaryButton,
} from 'components/common/Modal';
import EditTicketPopover from '../right-side-bar/components/dropdowns/client-view-dropdowns/components/tickets/components/EditTicketPopover';
import * as ListS from '../right-side-bar/components/dropdowns/Dropdowns.styled';

const TableWrap = styled.div`
    overflow-x: auto;
    overflow-y: auto;
    max-height: min(58vh, 520px);
    margin: 0 -4px;
    padding: 0 4px;
`;

type TicketsModalProps = {
    open: boolean;
    onClose: () => void;
};

export default function TicketsModal({ open, onClose }: TicketsModalProps) {
    const dispatch = useDispatch<AppDispatch>();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const { list: docsList, isLoading } = useSelector((state: RootState) => state.documentsApp);

    const fetchTickets = async (appID: string) => {
        try {
            const response = await api.tickets.getTicketsByApplication(appID);
            setTickets(response.data);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        setTickets([]);
        if (activeApp && open) {
            fetchTickets(activeApp.id.toString());
        }
    }, [activeApp, open]);

    useEffect(() => {
        if (!activeApp) return;
        const handleStorageChange = () => {
            const storedValue = localStorage.getItem('newTicket');
            if (storedValue === 'yes' && activeApp) {
                fetchTickets(activeApp.id.toString());
                localStorage.setItem('newTicket', 'no');
            }
        };
        window.addEventListener('storageValueChanged', handleStorageChange);
        return () => window.removeEventListener('storageValueChanged', handleStorageChange);
    }, [activeApp]);

    const handleChangeItem = async (id: number, checkedStatus: boolean) => {
        const target = tickets.find((ticket) => ticket.id === id);
        if (!target) return;
        const payload = {
            id: target.id,
            status: checkedStatus ? TicketStatus.CLOSED : TicketStatus.PENDING,
        };
        try {
            await api.tickets.editTicket(payload);
            if (activeApp) {
                fetchTickets(activeApp.id.toString());
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (!activeApp || !open) return;
        if (
            (docsList.length > 0 && activeApp.id !== docsList[0].application_id) ||
            docsList.length === 0
        ) {
            dispatch(loadDocs(activeApp.id.toString()));
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeApp, open]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString(language === 'EN' ? 'default' : 'fr', { month: 'short' });
        return `${day} ${month}`;
    };

    const title = t('tickets', { lng: language });
    const subtitle = t('ticketsModalSubtitle', {
        lng: language,
        defaultValue: 'Open requests linked to documents for this case.',
    });

    return (
        <>
            <Modal open={open} onClose={onClose} size="lg" ariaLabel={title}>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                    }}
                >
                    <ModalHeader
                        eyebrow={t('case', { lng: language, defaultValue: 'Case' })}
                        title={title}
                        subtitle={subtitle}
                    />
                    <Section>
                        {!activeApp ? (
                            <LTPlaceholder>
                                {t('selectCaseForTickets', {
                                    lng: language,
                                    defaultValue: 'Select a case to view tickets.',
                                })}
                            </LTPlaceholder>
                        ) : (
                            <>
                                <TableWrap>
                                    <ListS.ListTable>
                                        <LTHeader>
                                            <LTItem
                                                maxWidth="calc((95% - 101px) / 2)"
                                                minWidth="calc((95% - 101px) / 2)"
                                            >
                                                {t('document', { lng: language })}
                                            </LTItem>
                                            <LTItem
                                                maxWidth="calc((95% - 101px) / 2)"
                                                minWidth="calc((95% - 101px) / 2)"
                                            >
                                                {t('description', { lng: language })}
                                            </LTItem>
                                            <LTItem
                                                maxWidth="52px"
                                                minWidth="52px"
                                                style={{ textAlign: 'center' }}
                                            >
                                                {t('created', { lng: language })}
                                            </LTItem>
                                            <LTItem
                                                maxWidth="49px"
                                                minWidth="49px"
                                                style={{ textAlign: 'center' }}
                                            >
                                                {t('actions', { lng: language })}
                                            </LTItem>
                                        </LTHeader>
                                        {!isLoading && tickets.length === 0 && (
                                            <LTPlaceholder>
                                                {t('theListIsEmpty', { lng: language })}
                                            </LTPlaceholder>
                                        )}
                                        {!isLoading &&
                                            tickets.length > 0 &&
                                            tickets.map((ticket) => (
                                                <CustomLTRow
                                                    key={ticket.id}
                                                    id={ticket.id.toString()}
                                                >
                                                    <LTItem
                                                        maxWidth="calc((95% - 101px) / 2)"
                                                        minWidth="calc((95% - 101px) / 2)"
                                                    >
                                                        {ticket.document_name}
                                                    </LTItem>
                                                    <LTItem
                                                        maxWidth="calc((95% - 101px) / 2)"
                                                        minWidth="calc((95% - 101px) / 2)"
                                                    >
                                                        {ticket.text}
                                                    </LTItem>
                                                    <LTItem
                                                        maxWidth="52px"
                                                        minWidth="52px"
                                                        style={{ textAlign: 'center' }}
                                                    >
                                                        {formatDate(ticket.created.toString())}
                                                    </LTItem>
                                                    <LTItem maxWidth="49px" minWidth="49px">
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                gap: '0.5rem',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            <Tooltip
                                                                title={t('editTicket', {
                                                                    lng: language,
                                                                })}
                                                            >
                                                                <div className="this div makes the tooltip work">
                                                                    <EditTicketPopover
                                                                        ticket={ticket}
                                                                        fetchTickets={fetchTickets}
                                                                        docsList={docsList}
                                                                    />
                                                                </div>
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={t('closeTicket', {
                                                                    lng: language,
                                                                })}
                                                            >
                                                                <div className="this div makes the tooltip work">
                                                                    <Checkbox
                                                                        checked={
                                                                            ticket.status ===
                                                                            TicketStatus.CLOSED
                                                                        }
                                                                        onChange={(__, checked) =>
                                                                            handleChangeItem(
                                                                                ticket.id,
                                                                                checked
                                                                            )
                                                                        }
                                                                    />
                                                                </div>
                                                            </Tooltip>
                                                        </div>
                                                    </LTItem>
                                                    <LTMobileItem
                                                        name={t('document', { lng: language })}
                                                    >
                                                        {ticket.document_name}
                                                    </LTMobileItem>
                                                    <LTMobileItem
                                                        name={t('description', { lng: language })}
                                                    >
                                                        {ticket.text}
                                                    </LTMobileItem>
                                                    <LTMobileItem
                                                        name={t('created', { lng: language })}
                                                    >
                                                        {formatDate(ticket.created.toString())}
                                                    </LTMobileItem>
                                                    <LTMobileItem
                                                        name={t('actions', { lng: language })}
                                                    >
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <div
                                                                style={{ marginRight: '3px' }}
                                                                className="this div makes the tooltip work"
                                                            >
                                                                <EditTicketPopover
                                                                    ticket={ticket}
                                                                    fetchTickets={fetchTickets}
                                                                    docsList={docsList}
                                                                />
                                                            </div>
                                                            <Checkbox
                                                                checked={
                                                                    ticket.status ===
                                                                    TicketStatus.CLOSED
                                                                }
                                                                onChange={(__, checked) =>
                                                                    handleChangeItem(
                                                                        ticket.id,
                                                                        checked
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </LTMobileItem>
                                                </CustomLTRow>
                                            ))}
                                    </ListS.ListTable>
                                </TableWrap>
                            </>
                        )}
                    </Section>
                    <Actions>
                        <SecondaryButton type="button" onClick={onClose}>
                            {t('close', { lng: language, defaultValue: 'Close' })}
                        </SecondaryButton>
                    </Actions>
                </Form>
            </Modal>
        </>
    );
}
